import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../api';
import { useAuth } from '../contexts/AuthContext';

const SignIn = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const { signIn, error, clearError } = useAuth();
  const navigate = useNavigate();

  // Whether the admin has opened public signup. Starts false so the link never
  // flashes on a closed site while the check is in flight.
  const [signupEnabled, setSignupEnabled] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendNote, setResendNote] = useState('');

  useEffect(() => {
    let cancelled = false;
    authAPI.config()
      .then(c => { if (!cancelled) setSignupEnabled(!!c.signupEnabled); })
      .catch(() => {}); // unreachable backend = no link, which is the safe side
    return () => { cancelled = true; };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    if (error) {
      clearError();
      // The resend prompt belongs to the address that just failed — keeping it
      // visible while they type a different one would resend to the wrong place.
      setNeedsVerification(false);
      setResendNote('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    const result = await signIn(formData);
    if (result.success) {
      navigate('/chat');
    } else if (result.code === 'EMAIL_NOT_VERIFIED') {
      // Their password was right — they just never clicked the link. Offer the
      // fix here rather than making them find the original email.
      setNeedsVerification(true);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendNote('');
    try {
      await authAPI.resendVerification(formData.email.trim());
      setResendNote('Sent. Check your inbox, and your spam folder.');
    } catch {
      setResendNote('Could not resend right now. Please try again shortly.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black px-4">
      <div className="max-w-md w-full bg-gray-800/50 backdrop-blur-md rounded-2xl p-8 border border-gray-700/30 shadow-2xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            ROUT TECHNICAL AI
          </h1>
          <p className="text-gray-400">Welcome back</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
            {needsVerification && (
              <>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  className="mt-2 text-sm px-3 py-1.5 rounded-md bg-red-600/70 hover:bg-red-600 text-white disabled:opacity-60"
                >
                  {resending ? 'Sending…' : 'Resend confirmation email'}
                </button>
                {resendNote && <p className="text-teal-300 text-sm mt-2">{resendNote}</p>}
              </>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-3 bg-gray-700/50 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all ${
                errors.email ? 'border-red-500' : 'border-gray-600'
              }`}
              placeholder="Enter your email"
            />
            {errors.email && <p className="mt-1 text-sm text-red-400">{errors.email}</p>}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={`w-full px-4 py-3 bg-gray-700/50 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all ${
                errors.password ? 'border-red-500' : 'border-gray-600'
              }`}
              placeholder="Enter your password"
            />
            {errors.password && <p className="mt-1 text-sm text-red-400">{errors.password}</p>}
          </div>

          <button
            type="submit"
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          >
            Sign In
          </button>
        </form>

        <div className="mt-6 text-center">
          {signupEnabled ? (
            <p className="text-gray-400">
              Don't have an account?{' '}
              <Link to="/signup" className="text-blue-400 hover:text-blue-300 font-medium">
                Start a free trial
              </Link>
            </p>
          ) : (
            <p className="text-gray-400">
              Don't have an account? Contact your administrator.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignIn;
